import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WHATS_NEW } from '../config/whatsNew';
import { useSpotlight, WHATS_NEW_PREFIX } from '../context/SpotlightContext';
import { track } from '../services/analytics';
import { useUser } from './UserContext';

// Noon keeps the calendar day stable in every timezone
const formatDate = (isoDate) =>
  new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * "What's new" link in the header, with an unread dot and a panel of dated entries.
 * It is the default way staff hear about changes, and it never interrupts.
 * Entries live in config/whatsNew.js.
 */
const WhatsNew = ({ setIsMenuOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Entries that were unread when the panel opened, so they keep their "New" label after
  // opening marks them seen
  const [newAtOpen, setNewAtOpen] = useState([]);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const { seenLoaded, hasSeenAnnouncement, markWhatsNewSeen, requestSpotlight } = useSpotlight();
  const { locationInfo } = useUser();

  // Unread means not opened yet and not from before this person joined. Nothing is unread
  // until seen state loads, which also covers impersonation.
  const joinedOn = locationInfo?.date_joined?.slice(0, 10);
  const isUnread = (entry) =>
    seenLoaded
    && !hasSeenAnnouncement(WHATS_NEW_PREFIX + entry.id)
    && (!joinedOn || entry.date >= joinedOn);
  const hasUnread = WHATS_NEW.some(isUnread);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const toggle = () => {
    if (!isOpen) {
      track('whats_new_opened', { had_unread: hasUnread });
      setNewAtOpen(WHATS_NEW.filter(isUnread).map(entry => entry.id));
      markWhatsNewSeen(WHATS_NEW.map(entry => entry.id));
    }
    setIsOpen(!isOpen);
  };

  const showMe = (entry) => {
    track('whats_new_link_clicked', { entry_id: entry.id });
    setIsOpen(false);
    setIsMenuOpen?.(false);
    navigate(entry.link.to);
    if (entry.link.featureId) {
      requestSpotlight(entry.link.featureId);
    }
  };

  return (
    <div ref={panelRef} className="relative sm:mr-2">
      <button
        type="button"
        data-attr="nav-whats-new"
        onClick={toggle}
        aria-expanded={isOpen}
        className="relative flex items-center text-teal-100 hover:text-white hover:bg-secondary/90 px-4 py-2 rounded-full font-medium transition-all duration-300 ease-in-out"
      >
        What's new
        {hasUnread && (
          <span
            className="absolute top-1.5 right-2 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-primary"
            aria-label="New updates"
          />
        )}
      </button>

      {isOpen && (
        <div className="sm:absolute sm:right-0 mt-2 w-full sm:w-96 bg-white text-gray-800 rounded-lg shadow-lg border border-border z-[100] overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-base font-semibold text-teal-800">What's new</h2>
            <p className="text-xs text-gray-500">Changes to Dentara, newest first.</p>
          </div>
          <ul className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {WHATS_NEW.map(entry => (
              <li key={entry.id} className="px-4 py-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatDate(entry.date)}</span>
                  {newAtOpen.includes(entry.id) && (
                    <span className="px-3 py-1 inline-flex items-center justify-center text-xs font-medium rounded-full whitespace-nowrap bg-amber-100 text-amber-800">
                      New
                    </span>
                  )}
                </div>
                <h3 className="mt-1 text-sm font-semibold text-gray-900">{entry.title}</h3>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">{entry.text}</p>
                {entry.link && (
                  <button
                    type="button"
                    data-attr="whats-new-show-me"
                    onClick={() => showMe(entry)}
                    className="mt-2 text-sm font-medium text-teal-700 hover:text-teal-800"
                  >
                    Show me →
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WhatsNew;
